import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import * as FileConstants from 'folder-items/FileConstants';
import {BlobLinkList, CollapsibleBlobLinkList} from 'notebook/data-tables/BlobLinkList';
import {SnapshotChipList} from 'notebook/mediocre/core-ui/SnapshotChip';
import getMaxChipsToDisplay from 'notebook/tables/export/getMaxChipsToDisplay';
import {convertTypedValueBlob} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {getStorableTypeNameForId} from 'samples/SampleUtil';
import {
  FieldType,
  LINKED_FIELD_ITEM_NAMES,
  SchemaFieldRenderData,
  UNIT_AWARE_FIELD_TYPES,
} from 'schemas/fields/SchemaFieldConstants';
import {restBlobToGraphQL} from 'schemas/tags/BlobUtils';
import ComputedStateText from 'schemas/tags/computed/ComputedStateText';
import {ComputedState} from 'schemas/tags/computed/ComputedTagConstants';
import * as TagUtil from 'schemas/tags/TagUtil';
import {isItemLinkFieldType} from 'tag-schema/util/TagSchemaUtil';
import {ChipSize} from 'ui-library/chips/Chip';
import {CHIP_DISPLAY_LIMIT_TABLE_ROW} from 'ui-library/chips/ChipConstants';
import ChipList from 'ui-library/chips/ChipList';
import SummaryChip from 'ui-library/chips/SummaryChip';
import DatetimeValue from 'ui-library/DatetimeValue';
import DateValue from 'ui-library/DateValue';
import UrlAwareText from 'ui-library/UrlAwareText';
import MetadataEditorUnitAwareTagValueViewer from 'units/MetadataEditorUnitAwareTagValueViewer';
import * as DateUtil from 'util/DateUtil';

function getSummaryChipLabel(linkedItems: Array<FileConstants.TargetLinkable>, fieldType: FieldType): string {
  let label = LINKED_FIELD_ITEM_NAMES[fieldType];
  if (fieldType === FieldType.STORABLE_LINK) {
    const firstId = linkedItems[0] ? linkedItems[0].id : null;
    if (firstId == null) {
      return 'Sample';
    }
    label = getStorableTypeNameForId(firstId);
  }

  if (label == null) {
    label = 'Sample';
  }

  return label;
}

interface TagValueTypedValueViewerProps {
  chipSize: ChipSize;
  placeholder?: string | null;
  shouldPreviewImages: boolean;
  showChipsAsLinks: boolean;
  summarize: boolean;
  summarizeThreshold: number;
  typedValue: TypedValue | null;
  textValue: string;
  schemaFieldRenderData: SchemaFieldRenderData;
  shouldShowDateAndDatetimesAsChips: boolean;
  shouldCollapseBlobs?: boolean;
  isExportView?: boolean;
  showSummaryChipsAsPopover?: boolean;
  fileId?: string;
}

/**
 * Component for rendering typed values that correspond to tag values.
 */
export default function TagValueTypedValueViewer({
  chipSize,
  fileId,
  placeholder,
  schemaFieldRenderData,
  shouldPreviewImages,
  shouldCollapseBlobs = true,
  shouldShowDateAndDatetimesAsChips,
  showChipsAsLinks,
  showSummaryChipsAsPopover,
  summarize,
  summarizeThreshold,
  textValue,
  typedValue,
  isExportView = false,
}: TagValueTypedValueViewerProps): JSX.Element | null {
  const fieldType = schemaFieldRenderData.fieldType;

  if (typedValue == null) {
    return <UrlAwareText textValue={textValue} className={css(styles.text)} placeholder={placeholder} />;
  }
  if (typedValue.type === BaseTypedValueType.COMPUTING) {
    return (
      <ComputedStateText
        computedState={typedValue.computedState as ComputedState}
        schemaFieldRenderData={schemaFieldRenderData}
      />
    );
  } else if (typedValue.type === FieldType.BLOB_LINK) {
    const blobListProps = {
      blobs: typedValue.targetBlobs.map((blob) => restBlobToGraphQL(convertTypedValueBlob(blob))),
      chipSize,
      shouldPreviewImages,
    };
    const targetBlobs = typedValue.targetBlobs ?? [];
    return shouldCollapseBlobs ? (
      <CollapsibleBlobLinkList {...blobListProps} />
    ) : (
      <SnapshotChipList
        documentId={fileId}
        size={ChipSize.TABLE_CELL}
        ids={targetBlobs.map(({id}) => id)}
        className="chip-container"
      >
        <BlobLinkList {...blobListProps} />
      </SnapshotChipList>
    );
  } else if (isItemLinkFieldType(fieldType)) {
    const linksKey = TagUtil.getLinksKeyFromFieldType(fieldType);
    // @ts-expect-error TS doesn't know if links key is a valid prop in typedValue
    const linkedItems: Array<FileConstants.TargetLinkable> = linksKey ? typedValue[linksKey] : null;
    if (linkedItems != null) {
      if (summarize && linkedItems.length >= summarizeThreshold) {
        // A special summary view is available. This collapses a list of items into a single chip.
        return (
          <SummaryChip
            size={chipSize}
            itemSummarizer={(item) => TagUtil.maybeGetLinkedItemDisplayName(item)}
            itemLabel={getSummaryChipLabel(linkedItems, fieldType)}
            items={linkedItems}
            isPopover={showSummaryChipsAsPopover}
          />
        );
      } else if (linkedItems.length > 0) {
        return (
          <ChipList
            itemIds={linkedItems.map((item) => item.id)}
            displayNameOverrides={linkedItems.map((item) => TagUtil.maybeGetLinkedItemDisplayName(item))}
            maxChips={getMaxChipsToDisplay(CHIP_DISPLAY_LIMIT_TABLE_ROW, isExportView)}
            chipSize={chipSize}
            showChipsAsLinks={showChipsAsLinks}
            isVertical={false}
          />
        );
      }
    }
  } else if (
    typedValue.type === FieldType.DATETIME &&
    typedValue.value != null &&
    typedValue.value.length > 0
  ) {
    if (shouldShowDateAndDatetimesAsChips) {
      return (
        <span>
          <DatetimeValue datetimeValue={typedValue.datetimeValue} />
        </span>
      );
    } else {
      return (
        <span>
          {typedValue.datetimeValue
            ? DateUtil.readableTime(typedValue.datetimeValue, {showSeconds: true, showTimezoneOffset: true})
            : typedValue.datetimeValue}
        </span>
      );
    }
  } else if (typedValue.type === FieldType.DATE && typedValue.value != null && typedValue.value.length > 0) {
    if (shouldShowDateAndDatetimesAsChips) {
      return (
        <span>
          <DateValue dateValue={typedValue.dateValue} />
        </span>
      );
    } else {
      return (
        <span>
          {typedValue.dateValue
            ? DateUtil.readableTime(typedValue.dateValue, {showTime: false})
            : typedValue.value}
        </span>
      );
    }
  } else if (schemaFieldRenderData.unitSymbol != null && UNIT_AWARE_FIELD_TYPES.includes(fieldType)) {
    return (
      <MetadataEditorUnitAwareTagValueViewer
        textValue={textValue}
        unitSymbol={schemaFieldRenderData.unitSymbol}
      />
    );
  }
  return (
    <UrlAwareText
      textValue={typedValue.type === FieldType.BOOLEAN ? textValue.toUpperCase() : textValue}
      className={css(styles.text)}
      placeholder={placeholder}
    />
  );
}

export const styles = StyleSheet.create({
  text: {
    maxWidth: '100%',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
  },
});
